import { api } from '../../core/network/api';
import { initLandingAudio, createMuteToggle } from '../landing-audio';
import { mountClickSound } from '../click-sound';
import { sanitizeCmsHtmlWithSafeLinks } from '../sanitize-html';

interface NewsArticle {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  imageUrl: string | null;
  tag: string;
  publishedAt: string | null;
}

interface NewsListResponse {
  items: NewsArticle[];
  page: number;
  pageSize: number;
  total: number;
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
}

const TAG_STYLE: Record<string, string> = {
  update: 'border-gold/40 text-gold',
  event: 'border-crimson/40 text-crimson',
  spotlight: 'border-green-400/40 text-green-400',
  news: 'border-text-dim/40 text-text-dim',
};

function tagBadge(tag: string): string {
  const style = TAG_STYLE[tag] ?? TAG_STYLE.news;
  return `<span class="text-[9px] font-heading uppercase tracking-[0.14em] border rounded px-1.5 py-0.5 ${style}">${escapeHtml(tag)}</span>`;
}

const SECTION = 'px-5 sm:px-8 max-w-4xl mx-auto';
const SECTION_WIDE = 'px-5 sm:px-8 max-w-6xl mx-auto';

function mainArticleCard(a: NewsArticle, spanTwo: boolean): string {
  return `
    <a href="/news/${escapeHtml(a.slug)}" data-nav
      class="group glass-panel overflow-hidden flex flex-col hover:border-gold/50 hover:no-underline transition-colors duration-200 cursor-pointer${spanTwo ? ' lg:col-span-2' : ''}">
      ${a.imageUrl ? `<div class="w-full aspect-video overflow-hidden bg-surface-2">
        <img src="${escapeHtml(a.imageUrl)}" alt="" loading="lazy" class="w-full h-full object-cover" />
      </div>` : ''}
      <div class="p-5 flex flex-col gap-3">
        <div class="flex items-center gap-2 flex-wrap">
          ${tagBadge(a.tag)}
          <span class="text-[10px] text-text-dim">${formatDate(a.publishedAt)}</span>
        </div>
        <h2 class="font-heading text-lg sm:text-xl text-text group-hover:text-gold transition-colors duration-200">${escapeHtml(a.title)}</h2>
        <p class="text-sm text-text-dim leading-relaxed line-clamp-3">${escapeHtml(a.summary)}</p>
      </div>
    </a>
  `;
}

function asideArticleCard(a: NewsArticle): string {
  return `
    <a href="/news/${escapeHtml(a.slug)}" data-nav
      class="group glass-panel overflow-hidden flex gap-0 hover:border-gold/50 hover:no-underline transition-colors duration-200 cursor-pointer">
      ${a.imageUrl ? `<div class="w-20 shrink-0 aspect-square overflow-hidden bg-surface-2">
        <img src="${escapeHtml(a.imageUrl)}" alt="" loading="lazy" class="w-full h-full object-cover" />
      </div>` : ''}
      <div class="p-3 flex flex-col gap-1.5 min-w-0 justify-center">
        ${tagBadge(a.tag)}
        <h3 class="font-heading text-xs text-text group-hover:text-gold transition-colors duration-200 line-clamp-2">${escapeHtml(a.title)}</h3>
        <span class="text-[10px] text-text-dim">${formatDate(a.publishedAt)}</span>
      </div>
    </a>
  `;
}

function otherArticleRow(a: NewsArticle): string {
  return `
    <a href="/news/${escapeHtml(a.slug)}" data-nav
      class="group glass-panel overflow-hidden flex items-center gap-4 px-4 py-3 hover:border-gold/50 hover:no-underline transition-colors duration-200 cursor-pointer">
      ${a.imageUrl ? `<div class="w-14 h-14 shrink-0 overflow-hidden rounded bg-surface-2">
        <img src="${escapeHtml(a.imageUrl)}" alt="" loading="lazy" class="w-full h-full object-cover" />
      </div>` : ''}
      <div class="flex flex-col gap-1 min-w-0 flex-1">
        <div class="flex items-center gap-2 flex-wrap">
          ${tagBadge(a.tag)}
          <span class="text-[10px] text-text-dim">${formatDate(a.publishedAt)}</span>
        </div>
        <h3 class="font-heading text-xs sm:text-sm text-text group-hover:text-gold transition-colors duration-200 truncate">${escapeHtml(a.title)}</h3>
      </div>
    </a>
  `;
}

export function renderNewsList(root: HTMLElement, onNavigate: (path: string) => void): () => void {
  root.innerHTML = `
    <div class="w-full h-full overflow-y-auto bg-bg text-text">
      <header class="sticky top-0 z-20 bg-bg/90 backdrop-blur border-b border-border">
        <div class="${SECTION_WIDE} py-3 flex items-center justify-between gap-4">
          <a href="/" class="flex items-center min-w-0 hover:no-underline">
            <img src="/brand/logo-wide-sm.webp" alt="Dune in Conflict" class="h-9 sm:h-11 w-auto shrink-0" />
          </a>
          <nav class="flex items-center gap-4 text-xs text-text-dim">
            <a href="/" data-nav class="hover:text-text hover:no-underline transition-colors duration-[120ms]">Home</a>
            <span class="text-gold">News</span>
          </nav>
        </div>
      </header>

      <div class="${SECTION_WIDE} py-10 sm:py-14 flex flex-col gap-10">
        <h1 class="font-heading text-lg sm:text-xl text-gold">News &amp; Updates</h1>
        <div data-news-list class="flex flex-col gap-10">
          <div class="text-text-dim text-sm">Loading...</div>
        </div>
      </div>
    </div>
  `;

  const listEl = root.querySelector('[data-news-list]') as HTMLElement;

  void api.get<NewsListResponse>('/cms/news').then((res) => {
    if (!res.items || res.items.length === 0) {
      listEl.innerHTML = '<p class="text-text-dim text-sm">No articles yet.</p>';
      return;
    }
    const [main, ...rest] = res.items;
    const aside = rest.slice(0, 3);
    const others = rest.slice(3);

    listEl.innerHTML = `
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
        ${mainArticleCard(main, aside.length > 0)}
        ${aside.length > 0 ? `<div class="flex flex-col gap-4">${aside.map(asideArticleCard).join('')}</div>` : ''}
      </div>
      ${others.length > 0 ? `
        <div class="flex flex-col gap-3">
          <h2 class="font-heading text-xs uppercase tracking-[0.14em] text-text-dim">More Articles</h2>
          ${others.map(otherArticleRow).join('')}
        </div>
      ` : ''}
    `;
  }).catch(() => {
    listEl.innerHTML = '<p class="text-text-dim text-sm">Failed to load articles.</p>';
  });

  function onClick(e: MouseEvent): void {
    const link = (e.target as HTMLElement).closest('a[data-nav]') as HTMLAnchorElement | null;
    if (!link) return;
    e.preventDefault();
    onNavigate(link.pathname);
  }

  root.addEventListener('click', onClick);

  const audioTeardown = initLandingAudio();
  const muteToggle = createMuteToggle();
  document.body.appendChild(muteToggle.el);
  const clickTeardown = mountClickSound(root);

  return () => {
    root.removeEventListener('click', onClick);
    clickTeardown();
    muteToggle.destroy();
    audioTeardown();
  };
}

export function renderNewsArticle(root: HTMLElement, slug: string, onNavigate: (path: string) => void): () => void {
  root.innerHTML = `
    <div class="w-full h-full overflow-y-auto bg-bg text-text">
      <header class="sticky top-0 z-20 bg-bg/90 backdrop-blur border-b border-border">
        <div class="${SECTION} py-3 flex items-center justify-between gap-4">
          <a href="/" class="flex items-center min-w-0 hover:no-underline">
            <img src="/brand/logo-wide-sm.webp" alt="Dune in Conflict" class="h-9 sm:h-11 w-auto shrink-0" />
          </a>
          <nav class="flex items-center gap-4 text-xs text-text-dim">
            <a href="/" data-nav class="hover:text-text hover:no-underline transition-colors duration-[120ms]">Home</a>
            <a href="/news" data-nav class="hover:text-text hover:no-underline transition-colors duration-[120ms]">News</a>
          </nav>
        </div>
      </header>

      <div data-article class="${SECTION} py-10 sm:py-14 flex flex-col gap-6">
        <div class="text-text-dim text-sm">Loading...</div>
      </div>
    </div>
  `;

  const articleEl = root.querySelector('[data-article]') as HTMLElement;

  void api.get<NewsArticle>(`/cms/news/${encodeURIComponent(slug)}`).then((a) => {
    articleEl.innerHTML = `
      <a href="/news" data-nav class="text-xs text-text-dim hover:text-gold transition-colors duration-[120ms]">&larr; All articles</a>
      ${a.imageUrl ? `<div class="w-full aspect-video rounded overflow-hidden bg-surface-2">
        <img src="${escapeHtml(a.imageUrl)}" alt="" class="w-full h-full object-cover" />
      </div>` : ''}
      <div class="flex items-center gap-2 flex-wrap">
        ${tagBadge(a.tag)}
        <span class="text-[10px] text-text-dim">${formatDate(a.publishedAt)}</span>
      </div>
      <h1 class="font-heading text-lg sm:text-xl text-gold">${escapeHtml(a.title)}</h1>
      <div class="prose-game text-sm text-text-dim leading-relaxed flex flex-col gap-4">
        ${sanitizeCmsHtmlWithSafeLinks(a.content)}
      </div>
      <a href="/news" data-nav class="inline-block mt-4 text-xs text-text-dim hover:text-gold transition-colors duration-[120ms]">&larr; Back to news</a>
    `;
  }).catch(() => {
    articleEl.innerHTML = `
      <p class="text-text-dim text-sm">Article not found.</p>
      <a href="/news" data-nav class="text-xs text-text-dim hover:text-gold transition-colors duration-[120ms]">&larr; All articles</a>
    `;
  });

  function onClick(e: MouseEvent): void {
    const link = (e.target as HTMLElement).closest('a[data-nav]') as HTMLAnchorElement | null;
    if (!link) return;
    e.preventDefault();
    onNavigate(link.pathname);
  }

  root.addEventListener('click', onClick);

  const audioTeardown = initLandingAudio();
  const muteToggle = createMuteToggle();
  document.body.appendChild(muteToggle.el);
  const clickTeardown = mountClickSound(root);

  return () => {
    root.removeEventListener('click', onClick);
    clickTeardown();
    muteToggle.destroy();
    audioTeardown();
  };
}
